import React, { useId, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { BookOpen, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDebounce } from "../../hooks/useDebounce";
import { portalArticleParam, useCustomerPortalSuggestions } from "../../hooks/useKnowledgeBase";
import { usePortalBranding } from "../../hooks/usePortalBranding";

interface KnowledgeBaseSearchBoxProps {
  value: string;
  onChange: (value: string) => void;
  // Runs the search (the results page)
  onSearch: (query: string) => void;
  orgSlug: string;
}

/**
 * The knowledge base's search box. As the visitor types (2 characters or more) it suggests the best-matching
 * articles, which open directly; Enter, the Search button or the last row runs a full search. A combobox: arrow keys
 * move through the suggestions, Enter opens the highlighted one, Escape closes the list.
 */
export const KnowledgeBaseSearchBox: React.FC<KnowledgeBaseSearchBoxProps> = ({ value, onChange, onSearch, orgSlug }) => {
  const navigate = useNavigate();
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // -1: nothing highlighted; suggestions are 0..n-1, and n is "Search for ..."
  const [active, setActive] = useState(-1);
  const { getContrastText, getThemedPrimaryColor, getThemedSecondaryColor, getThemedPrimaryWithOpacity } = usePortalBranding();

  const debounced = useDebounce(value.trim(), 250);
  const { data: suggestions = [] } = useCustomerPortalSuggestions(debounced);
  const query = value.trim();
  // What's listed must be for what's in the box now (debounced), not a moment ago
  const shown = query.length >= 2 && debounced === query ? suggestions : [];
  const isOpen = open && shown.length > 0;
  const searchRow = shown.length;

  const openArticle = (index: number) => {
    const article = shown[index];
    if (!article) return;
    setOpen(false);
    navigate({
      to: "/org/$orgSlug/portal/knowledge-base/$articleId",
      params: { orgSlug, articleId: portalArticleParam(article) },
    });
  };

  const runSearch = () => {
    if (!query) return;
    setOpen(false);
    setActive(-1);
    onSearch(query);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" && shown.length > 0) {
        setOpen(true);
        setActive(0);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      setActive((i) => (i >= searchRow ? 0 : i + 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setActive((i) => (i <= 0 ? searchRow : i - 1));
      e.preventDefault();
    } else if (e.key === "Escape") {
      setOpen(false);
      setActive(-1);
      e.preventDefault();
    } else if (e.key === "Enter" && active >= 0 && active < searchRow) {
      openArticle(active);
      e.preventDefault();
    }
  };

  const optionId = (index: number) => `${listboxId}-option-${index}`;
  const primary = getThemedPrimaryColor();

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        runSearch();
      }}
      className="max-w-2xl mx-auto px-4"
      role="search"
    >
      <div
        ref={containerRef}
        className="relative"
        onBlur={(e) => {
          // Moving focus within the box (to a suggestion) keeps the list open
          if (!containerRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
        }}
      >
        <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" aria-hidden="true" />
        <Input
          type="search"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={isOpen && active >= 0 ? optionId(active) : undefined}
          autoComplete="off"
          placeholder="Search"
          aria-label="Search the knowledge base"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onKeyDown={handleKeyDown}
          className="pl-10 sm:pl-12 pr-20 sm:pr-24 py-4 sm:py-6 text-base sm:text-lg border-2 border-gray-300 dark:border-gray-600"
          style={{ "--tw-ring-color": primary } as React.CSSProperties}
          onFocus={(e) => {
            e.target.style.borderColor = primary;
            setOpen(true);
          }}
          onBlurCapture={(e) => {
            e.target.style.borderColor = "";
          }}
        />
        <Button
          type="submit"
          size="sm"
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-sm sm:text-base"
          disabled={!query}
          style={{
            backgroundColor: query ? primary : undefined,
            borderColor: query ? primary : undefined,
            color: query ? getContrastText(primary) : undefined,
          }}
          onMouseEnter={(e) => {
            if (query) {
              e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
              e.currentTarget.style.borderColor = getThemedSecondaryColor();
              e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
            }
          }}
          onMouseLeave={(e) => {
            if (query) {
              e.currentTarget.style.backgroundColor = primary;
              e.currentTarget.style.borderColor = primary;
              e.currentTarget.style.color = getContrastText(primary);
            }
          }}
        >
          Search
        </Button>

        {isOpen && (
          <ul
            id={listboxId}
            role="listbox"
            aria-label="Suggested articles"
            className="absolute z-30 mt-2 w-full overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-left shadow-lg"
          >
            {shown.map((article, index) => (
              <li
                key={article.id}
                id={optionId(index)}
                role="option"
                aria-selected={active === index}
                // Keep focus in the box, so the click lands before the list closes
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => openArticle(index)}
                onMouseEnter={() => setActive(index)}
                className="flex cursor-pointer items-start gap-3 px-4 py-2.5"
                style={active === index ? { backgroundColor: getThemedPrimaryWithOpacity(0.08) } : undefined}
              >
                <BookOpen className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">{article.title}</span>
                  {(article.collectionPath || article.collectionName) && (
                    <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                      {article.collectionPath || article.collectionName}
                    </span>
                  )}
                </span>
              </li>
            ))}
            <li
              id={optionId(searchRow)}
              role="option"
              aria-selected={active === searchRow}
              onMouseDown={(e) => e.preventDefault()}
              onClick={runSearch}
              onMouseEnter={() => setActive(searchRow)}
              className="flex cursor-pointer items-center gap-3 border-t border-gray-100 dark:border-gray-700 px-4 py-2.5 text-sm"
              style={{
                color: primary,
                ...(active === searchRow ? { backgroundColor: getThemedPrimaryWithOpacity(0.08) } : {}),
              }}
            >
              <Search className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span className="truncate">Search all articles for "{query}"</span>
            </li>
          </ul>
        )}
      </div>
    </form>
  );
};
